import { makeAutoObservable, runInAction } from "mobx";
import React, { useContext } from "react";
import * as Models from "../models/models";
import { formulas, inputs, mods } from "./hardCode";
import { Apis } from "./apis";
import { AuthStore } from "./auth-store";
import { QueryCache, QueryClient, QueryClientProvider, useQuery as useReactQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { BaseUrl } from "./constants";

export class Store {
  private _queryClient: QueryClient;
  private _apis: Apis;
  constructor(queryClient: QueryClient, authStore: AuthStore) {
    this._queryClient = queryClient;
    this._apis = new Apis(authStore.ApiCaller);
    makeAutoObservable(this);
  }

  initialiseAuth = (authStore: AuthStore) => {
    this._apis = new Apis(authStore.ApiCaller);
    authStore.InitializeUsers();
  };

  initialise = async () => {
    await this.InitializeModules();
    await this.InitializeInputElements();
    await this.InitializeFormulaElements();
    await this.InitializeReportArchives();
    await this.InitializeReports();
    return true;
  };

  // modules

  modules: Models.Module[] = mods;
  moduleState: Models.LoadState = Models.LoadState.NotLoaded;
  lookupDataPageNoState: Models.LoadState = Models.LoadState.NotLoaded;

  async InitializeModules(isUpdate?: boolean) {
    if (this.moduleState === Models.LoadState.Loaded && isUpdate == null) {
      return;
    }
    this.moduleState = Models.LoadState.Loading;
    try {
      const modules = await this._apis.GetAllModules();
      runInAction(() => {
        this.modules = [...modules];
        this.moduleState = Models.LoadState.Loaded;
      });
    } catch (e) {
      runInAction(() => {
        this.modules = [];
        this.moduleState = Models.LoadState.Error;
      });
    }
  }
  async AddModule(data: Models.Module) {
    const result: { module: Models.Module; inputs: Models.InputElement[] } = await this._apis.AddModule(data);
    runInAction(() => {
      this.modules.push(result.module);
      result.inputs.forEach((i) => this.inputElements.push(i));
      this._queryClient.invalidateQueries([Models.QueryKeys.Modules]);
    });
  }
  async UpdateModule(data: Models.Module) {
    const module = await this._apis.UpdateModule(data.id.toString(), data);
    runInAction(() => {
      this.modules = this.modules.map((d) => {
        if (d.id === data.id) {
          return module;
        } else {
          return d;
        }
      });
      this._queryClient.invalidateQueries([Models.QueryKeys.Modules]);
    });
  }
  async DeleteModule(data: Models.Module) {
    await this._apis.DeleteModule(data.id.toString());
    runInAction(() => {
      this.modules = this.modules.filter((d) => d.id != data.id);
      this._queryClient.invalidateQueries([Models.QueryKeys.Modules]);
    });
  }

  async GetAllModules() {
    if (this.moduleState != Models.LoadState.Loaded) {
      await this.InitializeModules();
    }
    return this.modules;
  }

  GetModuleById = (moduleId: string) => {
    return this.modules.find((d) => d.id?.toString() === moduleId);
  };

  // Update Module Assumptions
  async UpdateModuleAssumptions(moduleId: number, assumptions: string) {
    const updatedData = await this._apis.UpdateModuleAssumptions(moduleId, assumptions);
    runInAction(() => {
      this.modules = this.modules.map((d) => {
        if (d.id === moduleId) {
          return updatedData;
        } else {
          return d;
        }
      });
      this._queryClient.invalidateQueries([Models.QueryKeys.Assumptions]);
    });
  }

  GetAssumptionsByModuleId = (moduleId: string) => {
    return this.modules.find((d) => d.id?.toString() === moduleId)?.moduleConfig?.assumptions ?? "";
  };

  // input Elements

  inputElements: Models.InputElement[] = inputs;
  inputElementsState: Models.LoadState = Models.LoadState.NotLoaded;
  async InitializeInputElements(isUpdate?: boolean) {
    if (this.inputElementsState === Models.LoadState.Loaded && isUpdate == null) {
      return;
    }
    this.inputElementsState = Models.LoadState.Loading;
    try {
      const inputElements = await this._apis.GetAllInputElements();
      runInAction(() => {
        this.inputElements = [...inputElements];
        this.inputElementsState = Models.LoadState.Loaded;
      });
    } catch (e) {
      runInAction(() => {
        this.inputElements = [];
        this.inputElementsState = Models.LoadState.Error;
      });
    }
  }
  GetInputByModule = (moduleId: string) => {
    return this.inputElements.filter((d) => d.moduleId?.toString() === moduleId);
  };
  async AddInputData(data: Models.InputElement) {
    const input = await this._apis.AddInputElements(data);
    runInAction(() => {
      this.inputElements.push(input);
    });
  }
  async UpdateInputData(data: Models.InputElement) {
    const input = await this._apis.UpdateInputElements(data.id.toString(), data);
    runInAction(() => {
      this.inputElements = this.inputElements.map((d) => {
        if (d.id === data.id) {
          return input;
        } else {
          return d;
        }
      });
    });
  }
  async DeleteInputData(id: number) {
    await this._apis.DeleteInputElements(id.toString());
    runInAction(() => {
      this.inputElements = this.inputElements.filter((d) => d.id != id);
    });
  }
  // Update Input Elements and Formula Elements Sort Order
  async UpdateSortOrder(moduleId: number, key: string, order: number[]) {
    const orderedElements = await this._apis.UpdateSortOrder(moduleId, key, order);
    runInAction(() => {
      this.modules = this.modules.map((d) => {
        if (d.id === moduleId) {
          return orderedElements;
        } else {
          return d;
        }
      });
    });
  }

  // Update Modules Sort Order
  async UpdateModulesSortOrder(order: number[]) {
    try {
      const orderedModules = await this._apis.UpdateModulesSortOrder(order);
      runInAction(() => {
        this.modules = this.modules.map((d) => {
          const updatedModule = orderedModules.find((m) => m.id == d.id);
          if (updatedModule) {
            return updatedModule;
          } else {
            return d;
          }
        });
        this._queryClient.invalidateQueries([Models.QueryKeys.Modules]);
      });
      return true;
    } catch (e) {
      return false;
    }
  }
  // formula Elements

  formulaElements: Models.FormulaElement[] = formulas;
  formulaElementsState: Models.LoadState = Models.LoadState.NotLoaded;
  async InitializeFormulaElements(isUpdate?: boolean) {
    if (this.formulaElementsState === Models.LoadState.Loaded && isUpdate == null) {
      return;
    }
    this.formulaElementsState = Models.LoadState.Loading;
    try {
      const formulaElements = await this._apis.GetAllFormulas();
      runInAction(() => {
        this.formulaElements = [...formulaElements];
        this.formulaElementsState = Models.LoadState.Loaded;
      });
    } catch (e) {
      runInAction(() => {
        this.formulaElements = [];
        this.formulaElementsState = Models.LoadState.Error;
      });
    }
  }
  GetFormulasByModule = (moduleId: string) => {
    return this.formulaElements.filter((d) => d.moduleId?.toString() === moduleId);
  };
  async AddFormula(data: Models.FormulaElement) {
    const formula = await this._apis.AddFormula(data);
    runInAction(() => {
      this.formulaElements.push(formula);
    });
  }
  async UpdateFormula(data: Models.FormulaElement) {
    const formula = await this._apis.UpdateFormula(data.id.toString(), data);
    runInAction(() => {
      this.formulaElements = this.formulaElements.map((d) => {
        if (d.id === data.id) {
          return formula;
        } else {
          return d;
        }
      });
    });
  }
  async DeleteFormula(id: number) {
    await this._apis.DeleteFormula(id.toString());
    runInAction(() => {
      this.formulaElements = this.formulaElements.filter((d) => d.id != id);
    });
  }

  // Selected Input Elements
  selectedInputs: {
    [key: string]: string | number | null;
  } | null = null;

  results: {
    [key: string]: string | number | null;
  }[] = [];
  resultErrors: Models.FormulaError[] = [];
  SetResults = (
    results: {
      [key: string]: string | number | null;
    }[] = []
  ) => {
    runInAction(() => {
      this.results = results;
    });
  };

  errorMessages: { [key: string]: string } = {};

  validateInputs(selectedInput: { [key: string]: string | number | null }) {
    Object.entries(selectedInput).forEach(([key, value]) => {
      const input = this.inputElements.find((input) => input.id.toString() === key);
      if (value === "" || value === null || value === undefined) {
        this.errorMessages[key] = "This field is required";
      } else if (value === 0) {
        delete this.errorMessages[key];
      } else if (input && input.inputConfig && input.type === "Number Input") {
        const { max, min } = input.inputConfig;
        if (max && Number(value) > max) {
          this.errorMessages[key] = `Max value should be ${max}`;
        } else if (min && Number(value) < min) {
          this.errorMessages[key] = `Min value should be ${min}`;
        } else {
          delete this.errorMessages[key];
        }
      } else {
        delete this.errorMessages[key];
      }
    });
    const selectedInputsErrorMessage = Object.keys(selectedInput).filter((input) => Object.keys(this.errorMessages).includes(input));
    if (selectedInputsErrorMessage.length == 0) {
      this.errorMessages = {};
    }
    return Object.keys(this.errorMessages).length === 0;
  }

  SetErrorMessages = (message: { [key: string]: string } = {}) => {
    runInAction(() => {
      this.errorMessages = message;
    });
  };

  SetErrors = (errors: Models.FormulaError[] = []) => {
    runInAction(() => {
      this.resultErrors = errors;
    });
  };

  UpdateSelectedInputs = (selectedInputs: { [key: string]: string | number | null } | null) => {
    runInAction(() => {
      this.selectedInputs = selectedInputs;
    });
  };
  async EvaluateFormulas(selectedInputs: { [key: string]: string | number | null }, moduleId: string) {
    try {
      const result = await this._apis.EvaluateFormulas(selectedInputs, moduleId);
      return result;
    } catch (e) {
      return null;
    }
  }

  async getReportInputElementsBasedOnReportId(reportId: string) {
    try {
      const result = await this._apis.GetReportInputElementsBasedOnReportId(reportId);
      return result;
    } catch (e) {
      return null;
    }
  }

  // Lookup Data
  lookupData: Models.LookupData[] = [];
  lookupDataPageNo: { [year: string]: string[] } = {};

  SetLookupData = (lookupData: Models.LookupData[] = []) => {
    runInAction(() => {
      this.lookupData = lookupData;
    });
  };

  async InitializeLookupPageNo(isUpdate?: boolean) {
    if (this.lookupDataPageNoState === Models.LoadState.Loaded && isUpdate == null) {
      return;
    }
    this.lookupDataPageNoState = Models.LoadState.Loading;
    try {
      const data = await this._apis.GetAllPageNo();
      runInAction(() => {
        this.lookupDataPageNo = data;
        this.lookupDataPageNoState = Models.LoadState.Loaded;
      });
    } catch (e) {
      runInAction(() => {
        this.lookupDataPageNo = {};
        this.lookupDataPageNoState = Models.LoadState.Error;
      });
    }
  }

  async GetAllLookupDataPageNo() {
    if (this.lookupDataPageNoState != Models.LoadState.Loaded) {
      await this.InitializeLookupPageNo();
    }
    return this.lookupDataPageNo;
  }

  async GetLookupDataByPageNo(pageNo: string, year: string, isUpdate?: boolean) {
    if (this.lookupData.filter((l) => l.pageNo === pageNo && l.year === year).length > 0 && isUpdate == null) {
      return this.lookupData;
    }
    try {
      const result = await this._apis.GetLookupDataByPageNo(pageNo, year);
      return result;
    } catch (e) {
      return [];
    }
  }

  async UploadLookupData(file: File) {
    try {
      const data = await this._apis.UploadLookupData(file);
      await this.InitializeLookupPageNo(true);
      this._queryClient.invalidateQueries([Models.QueryKeys.LookupDataPageNo]);
      return {
        error: false,
        message: data,
      };
    } catch (e) {
      return {
        error: true,
        message: (e as AxiosError).response?.data,
      };
    }
  }

  async DeleteAllLookupData() {
    try {
      await this._apis.DeleteLookupData();
      runInAction(() => {
        this.lookupDataPageNo = {};
        this._queryClient.invalidateQueries([Models.QueryKeys.LookupDataPageNo]);
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  // Import Export Module Data

  async ImportModuleData(name: string, parentId: number, file: File) {
    try {
      const result = await this._apis.ImportModule(name, parentId, file);
      runInAction(() => {
        this.modules.push(result.moduleData);
        result.inputElements.forEach((input) => {
          this.inputElements.push(input);
        });
        result.formulaElements.forEach((formula) => {
          this.formulaElements.push(formula);
        });
        this._queryClient.invalidateQueries([Models.QueryKeys.Modules]);
      });
      return {
        error: false,
        message: "",
      };
    } catch (e) {
      return {
        error: true,
        message: (e as AxiosError).response?.data,
      };
    }
  }

  async ExportModuleData(moduleId: number) {
    try {
      const result = await this._apis.ExportModule(moduleId);
      return result;
    } catch (e) {
      return false;
    }
  }

  GetClientLogoUrl() {
    return `${BaseUrl()}api/ImportExport/Logo?${Date.now()}`;
  }

  async UploadLogo(file: File) {
    try {
      await this._apis.UploadLogo(file);
      this._queryClient.invalidateQueries([Models.QueryKeys.Logo]);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Reports
  reports: Models.ReportData[] = [];
  reportsState: Models.LoadState = Models.LoadState.NotLoaded;

  async InitializeReports(isUpdate?: boolean) {
    if (this.reportsState === Models.LoadState.Loaded && isUpdate == null) {
      return;
    }
    this.reportsState = Models.LoadState.Loading;
    try {
      const reports = await this._apis.GetAllReports();
      runInAction(() => {
        this.reports = [...reports];
        this.reportsState = Models.LoadState.Loaded;
      });
    } catch (e) {
      runInAction(() => {
        this.reports = [];
        this.reportsState = Models.LoadState.Error;
      });
    }
  }

  async GetAllReports() {
    if (this.reportsState != Models.LoadState.Loaded) {
      await this.InitializeReports();
    }
    return this.reports;
  }

  GetReportById = (id: number) => {
    return this.reports.find((d) => d.id?.toString() === id.toString());
  };

  async SaveAsReport(reportData: Models.Report) {
    try {
      const report = await this._apis.SaveReport(reportData);
      runInAction(() => {
        this.reports.push({
          id: report.id,
          name: report.name,
          moduleId: report.moduleId,
          year: report.year,
        });
        this._queryClient.invalidateQueries([Models.QueryKeys.Reports]);
      });
      return {
        error: false,
        message: "",
      };
    } catch (e) {
      return {
        error: true,
        message: (e as AxiosError).response?.data,
      };
    }
  }

  async DeleteReport(id: number) {
    await this._apis.DeleteReport(id);
    runInAction(() => {
      this.reports = this.reports.filter((d) => d.id != id);
      this._queryClient.invalidateQueries([Models.QueryKeys.Reports]);
    });
  }
  async DownloadReportsData(moduleId: number, selectedReports: number[]) {
    try {
      const result = await this._apis.DownloadReports(moduleId, selectedReports);
      this.InitializeReportArchives(true);
      return result;
    } catch (e) {
      return false;
    }
  }

  async DownloadInterModuleReportsData(selectedReports: number[]) {
    try {
      const result = await this._apis.DownloadInterModuleReports(selectedReports);
      this.InitializeReportArchives(true);
      return result;
    } catch (e) {
      return false;
    }
  }

  async DownloadModuleAssumption(id: number) {
    try {
      const result = await this._apis.DownloadModuleAssumptions(id);
      return result;
    } catch (e) {
      return false;
    }
  }

  // Report Archives
  reportArchives: Models.ReportArchive[] = [];
  reportArchivesState: Models.LoadState = Models.LoadState.NotLoaded;

  async InitializeReportArchives(isUpdate?: boolean) {
    if (this.reportArchivesState === Models.LoadState.Loaded && isUpdate == null) {
      return;
    }
    this.reportArchivesState = Models.LoadState.Loading;
    try {
      const reportArchives = await this._apis.GetAllReportsArchives();
      runInAction(() => {
        this.reportArchives = [...reportArchives];
        this.reportArchivesState = Models.LoadState.Loaded;
      });
    } catch (e) {
      runInAction(() => {
        this.reportArchives = [];
        this.reportArchivesState = Models.LoadState.Error;
      });
    }
  }
  async GetAllReportsArchives() {
    if (this.reportArchivesState != Models.LoadState.Loaded) {
      await this.InitializeReportArchives();
    }
    return this.reportArchives;
  }
  async DeleteReportArchive(id: number) {
    await this._apis.DeleteReportArchive(id);
    runInAction(() => {
      this.reportArchives = this.reportArchives.filter((d) => d.id != id);
      this._queryClient.invalidateQueries([Models.QueryKeys.ReportArchives]);
    });
  }
  downloadReportArchive = async (id: string) => {
    const url = `${BaseUrl()}api/Reports/Download/ReportArchive/${id}`;
    const iframe = document.querySelector("#downloadFrame") as HTMLIFrameElement;
    iframe.src = url;
  };
}
const queryClient = new QueryClient({ queryCache: new QueryCache({}) });
const authStoreInstance = new AuthStore(queryClient);
const storeInstance = new Store(queryClient, authStoreInstance);

export interface Stores {
  authStore: AuthStore;
  store: Store;
}
const allStore: Stores = {
  store: storeInstance,
  authStore: authStoreInstance,
};
const StoreContext = React.createContext<Stores>(allStore);
export const StoreContextProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <StoreContext.Provider value={allStore}>{children}</StoreContext.Provider>
    </QueryClientProvider>
  );
};
export const useStore = () => {
  return useContext(StoreContext);
};

export function useQuery<TQueryFnData = unknown, TError = unknown, TData = TQueryFnData>(
  options: Omit<UseQueryOptions<TQueryFnData, TError, TData>, "initialData"> & {
    initialData?: () => undefined;
  }
): UseQueryResult<TData, TError> {
  return useReactQuery({
    queryKey: options.queryKey,
    queryFn: options.queryFn,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: options?.enabled ?? true,
    onSuccess: options.onSuccess,
  });
}
